/**
 * ui.js
 * UI rendering and interaction functions
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { escapeHtml } from './utils.js?v=302';
import { CONFIG, AGENTS } from './config.js?v=302';

/**
 * Render empty state with greeting and suggestions
 */
export function renderEmptyState() {
  // Active agent
  const activeAgent = AGENTS.find(a => a.id === state.selectedAgent) || AGENTS[0];

  // Time-based greeting
  const hour = new Date().getHours();
  let greeting = "Good Morning";
  if (hour >= 12 && hour < 18) greeting = "Good Afternoon";
  if (hour >= 18 || hour < 5) greeting = "Good Evening";

  // Personalized suggestions with icons
  const suggestionsData = [
    {
      text: "Check my calendar",
      prompt: "What's on my calendar today?",
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>'
    },
    {
      text: "Check my email",
      prompt: "Any important unread emails?",
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>'
    },
    {
      text: "Task board status",
      prompt: "What's the status of my task board?",
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>'
    },
    {
      text: "Draft a LinkedIn post",
      prompt: "Help me draft a LinkedIn post about AI and productivity",
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>'
    }
  ];

  const suggestions = suggestionsData
    .map(
      (item) =>
        `<button type="button" class="suggestion-pill" data-prompt="${item.prompt}">${item.icon}${item.text}</button>`,
    )
    .join("");

  // Quick Actions
  const quickActions = `
      <div class="quick-actions">
        <div class="action-card" onclick="window.createNewChat()">
          <div class="action-card-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
          </div>
          <span class="action-card-label">New Chat</span>
        </div>
        <div class="action-card" onclick="document.getElementById('file-input').click()">
          <div class="action-card-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
          </div>
          <span class="action-card-label">Upload</span>
        </div>
        <div class="action-card" onclick="document.getElementById('search-chats').focus()">
          <div class="action-card-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </div>
          <span class="action-card-label">Search</span>
        </div>
        <div class="action-card" onclick="document.getElementById('settings-modal').classList.add('visible');">
          <div class="action-card-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
          </div>
          <span class="action-card-label">Settings</span>
        </div>
      </div>
    `;

  elements.messagesContainer.innerHTML = `
            <div class="empty-state">
                <div class="empty-state-avatar" aria-hidden="true">
                  <img class="ed-seal" src="dist/eliza-avatar.webp" alt="Eliza" />
                </div>
                <h1 class="empty-state-title">Welcome to ${activeAgent.name}</h1>
                <p class="empty-state-text">
                    ${activeAgent.id === 'main' ? 'Your personal AI assistant, always ready to help.' :
                      activeAgent.id === 'samantha' ? "Hi! I'm Samantha. I chose my own name. Want to talk?" :
                      activeAgent.id === 'nova' ? 'Research, content, and creative exploration.' :
                      activeAgent.id === 'spark' ? 'Code, infrastructure, and architecture.' :
                      activeAgent.id === 'lex' ? 'Legal analysis and compliance.' :
                      'Ready to help.'}
                    Start a new conversation or try one of these prompts to begin.
                </p>
                <div class="empty-state-divider" aria-hidden="true"></div>
                <div class="empty-state-suggestions">
                    ${suggestions}
                </div>
                ${quickActions}
            </div>
        `;

  // Re-attach event listeners for suggestion pills
  document.querySelectorAll(".suggestion-pill").forEach((pill) => {
    pill.addEventListener("click", () => {
      elements.input.value = pill.dataset.prompt;
      elements.input.focus();
    });
    pill.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        pill.click();
      }
    });
  });
}

/**
 * Render chat list in sidebar
 */
export function renderChatList() {
  if (state.chats.length === 0) {
    elements.chatList.innerHTML = `
                <div class="empty-state-sidebar">
                    <div class="empty-state-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                    </div>
                    <div class="empty-state-text">No conversations yet</div>
                    <div class="empty-state-sub">Start a new chat to begin</div>
                </div>
            `;
    return;
  }

  // Sort chats: pinned first, then newest first
  const sortedChats = [...state.chats].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    const dateA = new Date(a.createdAt || 0);
    const dateB = new Date(b.createdAt || 0);
    return dateB - dateA;
  });

  // Group chats
  const groups = {
    today: [],
    yesterday: [],
    week: [],
    earlier: []
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  sortedChats.forEach(chat => {
    const chatDate = new Date(chat.createdAt || Date.now());
    const chatDay = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());

    if (chatDay.getTime() === today.getTime()) {
      groups.today.push(chat);
    } else if (chatDay.getTime() === yesterday.getTime()) {
      groups.yesterday.push(chat);
    } else if (chatDay > weekAgo) {
      groups.week.push(chat);
    } else {
      groups.earlier.push(chat);
    }
  });

  let html = '';

  const renderGroup = (title, chats) => {
    if (chats.length === 0) return '';
    return `
            <div class="date-header">${title}</div>
            ${chats.map(chat => {
      const lastMsg = chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;
      let previewText = 'New conversation';
      if (lastMsg) {
        // Strip markdown for preview (regex-based, no DOM)
        previewText = lastMsg.content
          .replace(/```[\s\S]*?```/g, '[code]')
          .replace(/#{1,6}\s/g, '')
          .replace(/\*\*(.+?)\*\*/g, '$1')
          .replace(/\*(.+?)\*/g, '$1')
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
          .replace(/[`~_]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        previewText = previewText.substring(0, 50) + (previewText.length > 50 ? '...' : '');
      }

      const chatDate = new Date(chat.createdAt || Date.now());
      const timeStr = chatDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const pinIcon = chat.pinned
        ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1"><path d="M16 2L20.7 6.7C21.1 7.1 21.1 7.7 20.7 8.1L18 10.8L17.3 16.3C17.2 16.9 16.5 17.2 16 16.8L12 13.4L7.7 17.7C7.3 18.1 6.7 18.1 6.3 17.7C5.9 17.3 5.9 16.7 6.3 16.3L10.6 12L7.2 8C6.8 7.5 7.1 6.8 7.7 6.7L13.2 6L15.9 3.3C16.3 2.9 16.9 2.9 17.3 3.3"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 2L20.7 6.7C21.1 7.1 21.1 7.7 20.7 8.1L18 10.8L17.3 16.3C17.2 16.9 16.5 17.2 16 16.8L12 13.4L7.7 17.7C7.3 18.1 6.7 18.1 6.3 17.7C5.9 17.3 5.9 16.7 6.3 16.3L10.6 12L7.2 8C6.8 7.5 7.1 6.8 7.7 6.7L13.2 6L15.9 3.3C16.3 2.9 16.9 2.9 17.3 3.3"/></svg>';

      return `
                <div class="chat-item ${chat.id === state.currentChatId ? "active" : ""}${chat.pinned ? " pinned" : ""}" data-id="${chat.id}">
                    <div class="chat-item-header">
                        <span class="chat-title">${chat.pinned ? '📌 ' : ''}${escapeHtml(chat.title)}</span>
                        <span class="chat-time">${timeStr}</span>
                    </div>
                    <div class="chat-preview">${escapeHtml(previewText)}</div>

                    <div class="chat-actions">
                        <button class="action-btn pin-chat-btn${chat.pinned ? ' pinned' : ''}" data-id="${chat.id}" title="${chat.pinned ? 'Unpin' : 'Pin'} chat" aria-label="${chat.pinned ? 'Unpin' : 'Pin'} chat">
                            ${pinIcon}
                        </button>
                        <button class="action-btn rename rename-chat-btn" data-id="${chat.id}" title="Rename chat" aria-label="Rename chat">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        </button>
                        <button class="action-btn delete delete-chat-btn" data-id="${chat.id}" title="Delete chat" aria-label="Delete chat">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                    </div>
                </div>
                `;
    }).join('')}
        `;
  };

  html += renderGroup('Today', groups.today);
  html += renderGroup('Yesterday', groups.yesterday);
  html += renderGroup('Previous 7 Days', groups.week);
  html += renderGroup('Older', groups.earlier);

  elements.chatList.innerHTML = html;

  // Chat item click handlers - imported dynamically to avoid circular deps
  elements.chatList.querySelectorAll(".chat-item").forEach((item) => {
    item.addEventListener("click", (e) => {
      // Don't trigger if clicking actions
      if (e.target.closest('.chat-actions')) return;
      import('./chat.js?v=302').then(({ loadChat }) => {
        loadChat(item.dataset.id);
      });
    });
  });

  // Rename button handlers
  elements.chatList
    .querySelectorAll(".rename-chat-btn")
    .forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const chatId = btn.dataset.id;
        const chatItem = btn.closest('.chat-item');
        const titleSpan = chatItem.querySelector('.chat-title');
        const chat = state.chats.find(c => c.id === chatId);
        if (!chat) return;
        
        // Create input field
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'chat-title-input';
        input.value = chat.title;
        input.style.cssText = 'width: 100%; padding: 4px 8px; font-size: 13px; border: 1px solid var(--accent-primary); border-radius: 4px; background: var(--bg-secondary); color: var(--text-primary);';
        
        // Save function
        const saveName = () => {
          const newTitle = input.value.trim();
          if (newTitle && newTitle !== chat.title) {
            chat.title = newTitle;
            import('./storage.js?v=302').then(({ saveChats }) => {
              saveChats();
              renderChatList();
            });
          } else {
            renderChatList();
          }
        };
        
        // Replace title with input
        titleSpan.replaceWith(input);
        input.focus();
        input.select();
        
        // Handle save on Enter or blur
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            saveName();
          } else if (e.key === 'Escape') {
            renderChatList();
          }
        });
        input.addEventListener('blur', saveName);
      });
    });
  
  // Pin button handlers
  elements.chatList
    .querySelectorAll(".pin-chat-btn")
    .forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const chatId = btn.dataset.id;
        const chat = state.chats.find(c => c.id === chatId);
        if (!chat) return;
        chat.pinned = !chat.pinned;
        import('./storage.js?v=302').then(({ saveChats }) => {
          saveChats();
          renderChatList();
          showToast(chat.pinned ? "Chat pinned" : "Chat unpinned");
        });
      });
    });

  // Delete button handlers
  elements.chatList
    .querySelectorAll(".delete-chat-btn")
    .forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (confirm('Are you sure you want to delete this conversation?')) {
          import('./chat.js?v=302').then(({ deleteChat }) => {
            deleteChat(btn.dataset.id);
          });
        }
      });
    });

  // Announce chat count to screen readers
  const chatCount = state.chats.length;
  announceToScreenReader(`Loaded ${chatCount} conversation${chatCount !== 1 ? 's' : ''}`);
}

/**
 * Apply sidebar expanded/collapsed state
 */
export function applySidebarState() {
  elements.sidebar.classList.toggle("collapsed", !state.sidebarExpanded);
  elements.sidebarOverlay.classList.toggle(
    "visible",
    state.sidebarExpanded && window.innerWidth <= 768,
  );
}

/**
 * Toggle sidebar visibility
 */
export function toggleSidebar() {
  state.sidebarExpanded = !state.sidebarExpanded;
  localStorage.setItem(
    "chatbot_sidebar_state",
    state.sidebarExpanded ? "expanded" : "collapsed",
  );
  applySidebarState();
}

// Note: updateConnectionStatus has been removed from ui.js to prevent API pollution.
// The version in app.js is used instead, which just shows the model name without making API calls.

/**
 * Show toast notification
 * @param {string} message - Message to display
 * @param {string} type - Toast type (success, error, info)
 * @param {Object} options - Additional options (progress, actions)
 * @returns {Object} Toast control object with updateProgress and close methods
 */
export function showToast(message, type = "success", options = {}) {
  const { progress = null, actions = [] } = options;

  const toast = document.createElement("div");
  toast.className = `toast ${type}`;

  // Icons
  const icons = {
    success: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>',
    error: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>',
    info: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>'
  };

  const iconSvg = icons[type] || icons.info;

  // Structure
  toast.innerHTML = `
      <div class="toast-icon">${iconSvg}</div>
      <div class="toast-content">
        <div class="toast-message">${message}</div>
      </div>
    `;

  // Actions
  if (actions && actions.length > 0) {
    const actionsContainer = document.createElement("div");
    actionsContainer.className = "toast-actions";

    actions.forEach(action => {
      const btn = document.createElement("button");
      btn.className = "toast-action-btn";
      btn.textContent = action.label;
      btn.onclick = (e) => {
        e.stopPropagation();
        action.callback();
      };
      actionsContainer.appendChild(btn);
    });
    toast.appendChild(actionsContainer);
  }

  // Progress Bar
  let progressBar;
  if (progress !== null) {
    const progressContainer = document.createElement("div");
    progressContainer.className = "toast-progress";

    progressBar = document.createElement("div");
    progressBar.className = "toast-progress-bar";

    if (progress === "indeterminate") {
      progressBar.classList.add("indeterminate");
    } else {
      progressBar.style.width = `${Math.max(0, Math.min(100, progress))}%`;
    }

    progressContainer.appendChild(progressBar);
    toast.appendChild(progressContainer);
  }

  elements.toastContainer.appendChild(toast);

  const hide = () => {
    toast.classList.add("hiding");
    setTimeout(() => toast.remove(), 300);
  };

  if (progress === null) {
    setTimeout(hide, 3000);
  }

  return {
    updateProgress: (value) => {
      if (progressBar) {
        if (value === "indeterminate") {
          progressBar.classList.add("indeterminate");
          progressBar.style.width = "30%";
        } else {
          progressBar.classList.remove("indeterminate");
          progressBar.style.width = `${Math.max(0, Math.min(100, value))}%`;
        }
      }
    },
    close: hide
  };
}

/**
 * Scroll messages container to bottom
 */
export function scrollToBottom() {
  state.isReading = false;
  state.newMessageCount = 0;

  const btn = document.getElementById("scroll-bottom-btn");
  if (btn) {
    btn.classList.remove("has-new");
    const countSpan = btn.querySelector(".new-count");
    if (countSpan) countSpan.textContent = "";
  }

  elements.messagesContainer.scrollTo({
    top: elements.messagesContainer.scrollHeight,
    behavior: "smooth",
  });
}

/**
 * Handle scroll events for auto-scroll behavior
 */
export function handleScroll() {
  const container = elements.messagesContainer;
  const btn = document.getElementById("scroll-bottom-btn");
  if (!btn) return;

  // Track scroll position
  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;

  // Detect if user scrolled up
  const lastScrollTop = state.lastScrollTop || 0;
  if (scrollTop < lastScrollTop) {
    state.isReading = true;
  }
  state.lastScrollTop = scrollTop;

  // Check if we are at the bottom
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;

  if (isAtBottom) {
    state.isReading = false;
    state.newMessageCount = 0;
    btn.classList.remove("visible");
    btn.classList.remove("has-new");
    const countSpan = btn.querySelector(".new-count");
    if (countSpan) countSpan.textContent = "";
  } else {
    // Show button if we are not at the bottom
    btn.classList.add("visible");

    // If we have new messages, show the count
    if (state.newMessageCount > 0) {
      btn.classList.add("has-new");
      const countSpan = btn.querySelector(".new-count");
      if (countSpan) countSpan.textContent = `${state.newMessageCount} new`;
    }
  }
}

/**
 * Screen reader announcement helper
 * @param {string} message - Message to announce
 */
export function announceToScreenReader(message) {
  let statusEl = document.getElementById('chat-status');
  if (!statusEl) {
    statusEl = document.createElement('div');
    statusEl.id = 'chat-status';
    statusEl.className = 'sr-only';
    statusEl.setAttribute('aria-live', 'polite');
    statusEl.setAttribute('aria-atomic', 'true');
    document.body.appendChild(statusEl);
  }
  statusEl.textContent = '';
  setTimeout(function () {
    statusEl.textContent = message;
  }, 100);
}
